import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import Typography from '@/ui/components/typography'
import {
    Cell,
    StatusCell,
    StyledFdConfigTable,
    TableWrapper,
} from './FdConfigTable.styles'
import { useCustomTheme } from '@/core/theme'
import {
    TIntegration,
    TSecurityServiceName,
} from '@/core/modules/integrations/integrations'
import { FdConfigTableProps } from './FdConfigTable.types'
import { IntegrationsModule } from '@/core/modules/integrations'
import { Switch } from '@components/buttons/switch'
import { PopConfirm } from '@/ui/components/popConfirm'
import { SecurityServiceStateEnum } from '@atbay/productivity-integrations-node-sdk'
import { IntegrationStatus } from '@/ui/routes/integrations/integration/IntegrationsTable/IntegrationStatus'

const FdConfigTable = ({ data }: FdConfigTableProps) => {
    const theme = useCustomTheme()
    const columnHelper = createColumnHelper<TIntegration>()
    const [loadingByIntegrationId, setLoadingByIntegrationId] = useState<
        Record<string, boolean>
    >({})

    const getFdService = (integration: TIntegration) => {
        return integration.security_services?.find(
            (service) => service.name === 'fd'
        )
    }

    const onToggle = async (integration: TIntegration) => {
        const fdService = getFdService(integration)
        if (integration.id && fdService) {
            setLoadingByIntegrationId((prev) => ({
                ...prev,
                [integration.integration_id]: true,
            }))
            const enable = fdService.state === SecurityServiceStateEnum.DISABLED
            await IntegrationsModule.updateSecurityService(
                integration.id,
                integration.integration_id,
                fdService.id,
                (fdService.name as TSecurityServiceName) || 'fd',
                enable
            )
            setLoadingByIntegrationId((prev) => ({
                ...prev,
                [integration.integration_id]: false,
            }))
        }
    }

    const columns = [
        {
            ...columnHelper.accessor('external_name', {
                cell: (info) => (
                    <Cell data-testid="fd-config-table-account-name">
                        <Typography
                            variant="s-medium"
                            color={theme.colors.greys[700]}
                        >
                            {info.cell.getValue()}
                        </Typography>
                    </Cell>
                ),
                header: () => (
                    <Typography variant="s-bold">Account Name</Typography>
                ),
                enableSorting: false,
            }),
            width: '30%',
        },
        {
            ...columnHelper.accessor('name', {
                cell: (info) => (
                    <Cell data-testid="fd-config-table-application-type">
                        <Typography
                            variant="s-medium"
                            color={theme.colors.greys[700]}
                        >
                            {info.cell.getValue()}
                        </Typography>
                    </Cell>
                ),
                header: () => (
                    <Typography variant="s-bold">Application Type</Typography>
                ),
                enableSorting: false,
            }),
            width: '25%',
        },
        {
            ...columnHelper.accessor('status', {
                cell: (info) => (
                    <StatusCell
                        data-testid={`integrations-table-status-${info.cell.getValue()}`}
                        onClick={() => {}}
                    >
                        <IntegrationStatus
                            status={info.cell.getValue()}
                            type={info.row.original.type}
                        />
                    </StatusCell>
                ),
                header: () => (
                    <Typography variant="s-bold">Account Status</Typography>
                ),
                enableSorting: false,
            }),
            width: '20%',
        },
        {
            ...columnHelper.accessor('status', {
                cell: (info) => {
                    const isFdServiceEnabled =
                        getFdService(info.row.original)?.state === 'enabled'
                    const isFdIntegrationBroken =
                        info.row.original.status === 'broken'
                    return (
                        <StatusCell data-testid="fd-config-table-status">
                            {isFdServiceEnabled ? (
                                <PopConfirm
                                    dataTestId="fd-config-security-service-disable-popover"
                                    title="Disable Service"
                                    description="Are you sure you want to disable this service?"
                                    onConfirm={() =>
                                        onToggle(info.row.original)
                                    }
                                    okText="Disable"
                                    cancelText="Cancel"
                                    placement="left"
                                >
                                    <Switch
                                        loading={
                                            loadingByIntegrationId[
                                                info.row.original.id
                                            ]
                                        }
                                        checked={isFdServiceEnabled}
                                        testId="fd-config-table-status-switch-enabled"
                                        onChange={() => {}}
                                        disabled={isFdIntegrationBroken}
                                    />
                                </PopConfirm>
                            ) : (
                                <Switch
                                    loading={
                                        loadingByIntegrationId[
                                            info.row.original.id
                                        ]
                                    }
                                    checked={isFdServiceEnabled}
                                    onChange={() => onToggle(info.row.original)}
                                    testId="fd-config-table-status-switch-disabled"
                                    disabled={isFdIntegrationBroken}
                                />
                            )}
                        </StatusCell>
                    )
                },
                header: () => (
                    <Typography variant="s-bold">
                        Fraud Defense Status
                    </Typography>
                ),
                enableSorting: false,
            }),
            width: '25%',
        },
    ]

    return (
        <TableWrapper>
            {data.length > 0 && (
                <StyledFdConfigTable
                    data-testid="fd-config-table"
                    columns={columns as any}
                    data={data}
                    // @ts-ignore
                    tableHeight="fit-content"
                />
            )}
        </TableWrapper>
    )
}

export default FdConfigTable
